import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { ForbiddenState, LoadingState } from '@platform/ui-kit'
import { useAuth } from '@/app/providers/AuthProvider'
import type { PermissionKey } from '@/lib/permissions'
import { findModuleByKey, moduleDeclaredPermissions } from './moduleRegistry'
import type { ModuleChild } from './moduleRegistry'

/* ------------------------------------------------------------------
   Route Guard — سند مادر بند ۵.۲ و ۱۱.۱
   ------------------------------------------------------------------
   ⚠️ یادآوری همیشگی (بند ۷.۲):
      این Guardها فقط تجربه کاربری را درست می‌کنند.
      کنترل واقعی دسترسی در Backend Policy است و کاربر با تغییر URL
      نمی‌تواند به داده برسد، چون API مستقل بررسی می‌کند (AC-11).
   ------------------------------------------------------------------ */

/** مسیر اجباری تغییر رمز — تنها جایی که این نشانی نوشته می‌شود. */
export const CHANGE_PASSWORD_PATH = '/app/profile/password'

/**
 * ==================================================================
 * 🔴 مقصدِ «برگشت پس از ورود» هرگز نباید صفحهٔ تغییر رمز باشد.
 *
 * ⚠️ این صفحه یک مقصدِ **گذرا** است، نه جایی که کاربر قصدِ رفتن به
 * آن را داشته. اگر به‌عنوان `from` ذخیره شود، پس از ورودِ بعدی —
 * وقتی رمز دیگر عوض شده و Guard هم دلیلی برای هدایت ندارد —
 * LoginPage کاربر را دوباره به همان صفحه می‌فرستد و او در صفحهٔ
 * تغییر رمز گیر می‌کند، بدون اینکه هیچ‌چیزی واقعاً خراب باشد.
 *
 * 🔴 پس فیلتر همین‌جاست و نه در LoginPage: هر جایی که `from`
 * می‌سازد باید از همین یک تابع رد شود.
 * ==================================================================
 */
export function safeReturnPath(pathname: string, search = ''): string | undefined {
  const target = pathname + search

  // ⚠️ مقایسه روی مسیرِ خالص است، نه روی رشته‌ای که ممکن است
  //    query هم داشته باشد — وگرنه `?x=1` فیلتر را دور می‌زند.
  if (target.split('?')[0].startsWith(CHANGE_PASSWORD_PATH)) return undefined

  return target
}

/** فقط کاربر واردشده */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, initializing } = useAuth()
  const location = useLocation()

  if (initializing) return <LoadingState label="در حال بررسی وضعیت ورود…" className="min-h-dvh" />

  if (!user) {
    // مقصد اولیه نگه داشته می‌شود تا پس از ورود کاربر به همان‌جا برگردد
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: safeReturnPath(location.pathname, location.search) }}
      />
    )
  }

  // رمز موقت باید پیش از هر کاری عوض شود
  if (user.must_change_password && !location.pathname.startsWith(CHANGE_PASSWORD_PATH)) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />
  }

  return <>{children}</>
}

/** نیازمند مجوز مشخص */
export function RequirePermission({
  permission,
  anyOf,
  children,
}: {
  permission?: PermissionKey
  anyOf?: PermissionKey[]
  children: ReactNode
}) {
  const { can, canAny } = useAuth()

  const allowed = permission ? can(permission) : anyOf ? canAny(anyOf) : true
  if (!allowed) return <ForbiddenState />

  return <>{children}</>
}

/** نیازمند دسترسی به یک ماژول — برای محافظت از کل زیردرخت مسیر ماژول */
export function RequireModule({
  moduleKey,
  children,
}: {
  moduleKey: string
  children: ReactNode
}) {
  const { hasModuleAccess, canSeeModule } = useAuth()

  /*
   | ماژول‌های «فقط مدیر سیستم» با تایپ کردنِ نشانی هم باز نمی‌شوند.
   |
   | ⚠️ پنهان کردنِ منو به‌تنهایی کافی نیست: مسیر همچنان وجود دارد و
   | هر کسی می‌تواند `/app/access` را در نوار نشانی بنویسد. اینجا همان
   | قاعده‌ای اعمال می‌شود که سایدبار و داشبورد اعمال می‌کنند.
   |
   | 🔴 و باز هم این کنترلِ امنیتی نیست — Backend هر درخواست کنسول
   | دسترسی را مستقل می‌سنجد (بند ۷.۲ / AC-11).
   */
  const module = findModuleByKey(moduleKey)

  if (!canSeeModule(module)) {
    return (
      <ForbiddenState description="این بخش تنها در اختیار مدیر سیستم است." />
    )
  }

  /*
    ⚠️ کلیدها از رجیستری می‌آیند، نه از پیشوندِ نام ماژول.

    🔴 ماژول «سطح دسترسی» کلیدهایش `platform.*` است؛ با حدسِ پیشوندی
    برای همه بسته می‌ماند — که دقیقاً همان اتفاقی بود که افتاد.

    ⚠️ نسخهٔ اولِ این اصلاح، استخراج کلیدها را **همین‌جا** نوشته بود.
    سایدبار و کاشیِ داشبورد نسخهٔ خودشان را نداشتند و بدون کلید صدا
    می‌زدند، پس مسیر باز می‌شد ولی منو خاکستری می‌ماند — کاربر
    نمی‌توانست کلیک کند و دلیلش هیچ‌جا نوشته نبود.

    🔴 حالا یک تعریف در رجیستری است و `AuthProvider` هم پیش‌فرضش را از
    همان می‌گیرد. اینجا صریح فرستاده می‌شود چون خواننده باید ببیند
    تصمیم بر چه مبنایی است.
  */
  const declaredKeys = moduleDeclaredPermissions(moduleKey)

  if (!hasModuleAccess(moduleKey, declaredKeys)) {
    return (
      <ForbiddenState description="برای این ماژول هیچ دسترسی‌ای برای شما تعریف نشده است. در صورت نیاز از مدیر خود درخواست دسترسی کنید." />
    )
  }

  return <>{children}</>
}

/* ------------------------------------------------------------------
   انطباق مسیر با بخش ثبت‌شده در رجیستری
   ------------------------------------------------------------------ */

/**
 * بخشی از رجیستری که این مسیرِ نسبی به آن تعلق دارد.
 *
 * ⚠️ طولانی‌ترین تطبیق برنده است، و `''` فقط با ریشهٔ دقیق ماژول جور
 * می‌شود. وگرنه `''` پیشوندِ همه‌چیز است و مجوزِ صفحهٔ Landing به کل
 * ماژول تسری پیدا می‌کند.
 *
 * صادر شده تا تستِ همگامی هم دقیقاً همین منطق را بسنجد، نه کپی‌اش.
 */
export function matchRegistryChild(
  children: readonly ModuleChild[],
  relativePath: string,
): ModuleChild | undefined {
  const clean = relativePath.replace(/^\/+|\/+$/g, '')

  const candidates = children.filter((child) => {
    const childPath = child.path.replace(/^\/+|\/+$/g, '')

    if (childPath === '') return clean === ''

    return clean === childPath || clean.startsWith(`${childPath}/`)
  })

  return candidates.sort((a, b) => b.path.length - a.path.length)[0]
}

/**
 * دروازهٔ خودکارِ بخش‌های یک ماژول.
 *
 * ==================================================================
 * 🔴 نقصی که این کامپوننت بسته است.
 *
 * `moduleRegistry` برای هر بخش یک `permission` اعلام می‌کرد و همه
 * فکر می‌کردند این یعنی «آن بخش محافظت شده است». ولی آن اعلان فقط
 * **منوی کناری** را فیلتر می‌کرد.
 *
 * ۶ ماژول از ۱۰ هیچ‌وقت `RequirePermission` را دور مسیرهایشان
 * نگذاشته بودند. یعنی تایپ‌کردن نشانی در مرورگر، صفحه را باز می‌کرد،
 * صفحه درخواست می‌فرستاد، Backend ۴۰۳ می‌داد و کاربر یک «خطای
 * نامشخص» می‌دید.
 *
 * ⚠️ امنیت نشکسته بود — Backend همیشه بررسی می‌کند. ولی «اعلانِ مجوز»
 * و «اعمالِ مجوز» دو چیز بودند و هیچ‌چیز آن‌ها را به هم گره نمی‌زد.
 *
 * 🔴 راه‌حل: اعلان **همان** اعمال باشد. حالا رجیستری تنها منبع است و
 * افزودن یک بخش تازه با مجوز، خودبه‌خود محافظت می‌شود — بدون اینکه
 * کسی یادش بماند جای دیگری هم چیزی اضافه کند.
 * ==================================================================
 */
export function ModuleGuard({
  moduleKey,
  children,
}: {
  moduleKey: string
  children: ReactNode
}) {
  const { can, canAny } = useAuth()
  const location = useLocation()

  const module = findModuleByKey(moduleKey)

  if (!module) return <>{children}</>

  const base = `/app/${module.path}`
  const relative = location.pathname.startsWith(base)
    ? location.pathname.slice(base.length)
    : location.pathname

  const child = matchRegistryChild(module.children, relative)

  if (!child) return <>{children}</>

  const allowed = child.permission
    ? can(child.permission as PermissionKey)
    : child.anyOf
      ? canAny(child.anyOf as PermissionKey[])
      : true

  if (!allowed) return <ForbiddenState />

  return <>{children}</>
}
